import { useParams } from "@tanstack/react-router";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PublicLayout } from "../components/public-layout.js";
import acceptableUse from "../legal/acceptable-use.md?raw";
import { fillLegal, LEGAL } from "../legal/company.js";
import privacy from "../legal/privacy.md?raw";
import refunds from "../legal/refunds.md?raw";
import terms from "../legal/terms.md?raw";

/** The legal documents are written in English: that text is the binding one. */
export const LEGAL_DOCUMENTS: Record<string, string> = {
  terms,
  privacy,
  refunds,
  "acceptable-use": acceptableUse,
};

export function LegalPage() {
  const { t } = useTranslation();
  const { doc } = useParams({ from: "/legal/$doc" });
  const source = LEGAL_DOCUMENTS[doc];
  const html = useMemo(
    () =>
      source
        ? DOMPurify.sanitize(marked.parse(fillLegal(source), { gfm: true, async: false }), {
            USE_PROFILES: { html: true },
          })
        : null,
    [source],
  );
  return (
    <PublicLayout>
      <article className="mx-auto w-full max-w-3xl px-4 py-8">
        {LEGAL.draft ? (
          <p className="mb-6 rounded-xl bg-warn-soft px-4 py-3 text-sm text-warn">
            {t("public.legal.draft")}
          </p>
        ) : null}
        {html ? (
          <div
            className="legal-doc"
            // Our own documents, rendered from Markdown and sanitised all the same.
            // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitised with DOMPurify above
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-ink-2">{t("public.legal.notFound")}</p>
        )}
      </article>
    </PublicLayout>
  );
}
